import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Map as MapIcon, MapPinOff, Store } from 'lucide-react';
import { WorldPageHeader, WorldSection } from './WorldPageHeader';
import { NpcShopCard } from './NpcShopCard';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { EventChip } from './EventChip';
import { Disclosure } from './Disclosure';
import { CopyLinkButton } from '../guide/CopyLinkButton';
import { useWorld } from '../../contexts/WorldDataContext';
import { npcs } from '../../data/guide/npcs';
import { resolveNpcLocation } from '../../utils/npcLocation';
import { questsServedBy } from '../../utils/npcQuests';
import { NpcQuestServices } from './NpcQuestServices';
import { NPC_LOCATION_GAP, SHOP_INVENTORY_GAP } from '../../data/guide/technicalAudit';
import { imageEntryForMonster } from '../../utils/entityImages';
import { coordLabel, locationCopyText, mapName, mapPath, monsterPath } from '../../utils/worldFormat';
import { npcDisplayName, npcIdentity } from '../../data/guide/npcIdentity';
import type { Monster, WorldData } from '../../types/world';
const btn = 'inline-flex h-9 items-center gap-1.5 rounded-xl border border-white/10 px-3 text-xs font-semibold text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/40 hover:text-white';

/**
 * Ficha pública de un NPC. Misma ruta y mismo ID que antes, sin bloques de caza:
 * sin nivel, spots de farmeo, drops, HP ni estadísticas de combate. Los datos originales siguen en los archivos.
 */
export function NpcEntityView({
  w,
  m
}: {
  w: WorldData;
  m: Monster;
}) {
  const world = useWorld();
  const guideNpcs = npcs.filter(n => n.monsterId === m.id);
  const entry = imageEntryForMonster(w, m.id);
  const identity = npcIdentity(m.id);
  const title = guideNpcs.length === 1 ? guideNpcs[0].name : npcDisplayName(m.id, m.name);
  const mergedTarget = identity?.mergedInto !== undefined ? w.monsters.get(identity.mergedInto) : undefined;
  const mergedName = identity?.mergedInto !== undefined ? npcs.find(n => n.monsterId === identity.mergedInto)?.name ?? mergedTarget?.name : undefined;
  useEffect(() => {
    document.title = `${title} · NPC y tiendas · Guía MU Vanguard`;
  }, [title]);
  const spawns = w.idx.spawnsByMonster.get(m.id) ?? [];
  const locations = w.idx.locationsByMonster.get(m.id) ?? [];
  return <div data-id="element-1860" data-mp="pcwq8okt01eu">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'NPC y tiendas',
      to: '/guia/npc'
    }, {
      label: title
    }]} eyebrow="NPC" title={title} media={entry?.image ? <EntityImage entry={entry} alt={title} name={title} size="hero" lazy={false} kind="persona" data-id="element-1862" data-mp="ahqkm2pme4m1" /> : undefined} data-id="element-1861" data-mp="pd6loromb0rx">
        <p className="mt-3 max-w-2xl text-sm text-gray-300" data-id="element-1863" data-mp="5qi9914qc968">
          {guideNpcs.length ? guideNpcs.map(n => n.role).filter((v, i, a) => a.indexOf(v) === i).join(' · ') : questsServedBy(w, m.id).length ? 'Atiende misiones (37_quest_rules).' : 'Función no documentada en los datos de Vanguard.'}
        </p>
        {title !== m.name && <p className="mt-2 text-xs text-gray-500" data-id="element-1864" data-mp="34s1ode011z0h">Nombre en el catálogo: {m.name}</p>}
        {identity?.mergedInto !== undefined && <p className="mt-3 max-w-2xl rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-2 text-sm text-gray-200" data-id="element-1865" data-mp="2q46qzkb2r6h2">
            Mismo NPC que{' '}
            <Link to={monsterPath(identity.mergedInto)} className="font-semibold text-[#D4AF37] underline-offset-4 hover:underline" data-id="element-1866" data-mp="3irt5hnlubvip">
              {mergedName ?? `ID ${identity.mergedInto}`}
            </Link>{' '}
            según el staff. Su ubicación y servicios se publican en esa ficha.
          </p>}
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-1867" data-mp="3ds6snf9xchaf">
          <CopyLinkButton label="Copiar enlace" data-id="element-1868" data-mp="3duswp4xlwenw" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-1869" data-mp="1xd58b986asss">
        {guideNpcs.length > 0 ? <WorldSection id="servicios" title={guideNpcs.length > 1 ? 'Ubicaciones y servicios' : 'Ubicación y servicios'} count={guideNpcs.length > 1 ? guideNpcs.length : undefined} data-id="element-1870" data-mp="22ecg38abfrzg">
            <div className="grid gap-6 md:grid-cols-2" data-id="element-1871" data-mp="2dtpfwp4726vq">
              {guideNpcs.map(n => <NpcShopCard key={n.id} npc={n} loc={resolveNpcLocation(n, {
            status: world.status,
            data: world.data,
            error: world.error
          })} w={w} data-id="element-1872" data-mp="28u35sojnx6r5" />)}
            </div>
          </WorldSection> : <>
            <WorldSection id="ubicacion" title="Dónde está" count={spawns.length || undefined} data-id="element-1873" data-mp="31j1rfvmzur17">
              {spawns.length ? <ul className="grid gap-2.5 md:grid-cols-2" data-id="element-1874" data-mp="2rldseticuxd8">
                  {spawns.map(s => {
              const coords = coordLabel(s);
              const mn = mapName(w, s.mapId);
              return <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-white/10 p-3.5" data-id="element-1875" data-mp="1g6gzv9mypq14">
                        <span className="min-w-0" data-id="element-1876" data-mp="1b5t3e8fysgzi">
                          <span className="block font-semibold text-white" data-id="element-1877" data-mp="3mv8zbk38oxy1">{mn}</span>
                          <span className="block text-sm text-gray-400" data-id="element-1878" data-mp="3rtaqksatk3kl">{coords ?? 'Sin coordenadas en los archivos'} · aparición configurada en los archivos</span>
                          {s.eventId && <span className="mt-1 block" data-id="element-1879" data-mp="lg8h3cbwnqf"><EventChip w={w} eventId={s.eventId} data-id="element-1880" data-mp="5ehppi00czd6" /></span>}
                        </span>
                        <span className="flex flex-wrap gap-2" data-id="element-1881" data-mp="adu91sro7ob0">
                          <Link to={mapPath(s.mapId)} className={btn} data-id="element-1882" data-mp="aff1ncufcx68">
                            <MapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1883" data-mp="ecuor2euqats" /> Ver mapa
                          </Link>
                          {coords && <CopyTextButton text={locationCopyText(mn, m.name, s)} label="Copiar ubicación" data-id="element-1884" data-mp="octw4vzjbv52" />}
                        </span>
                      </li>;
            })}
                </ul> : locations.length ? <ul className="flex flex-wrap gap-2" data-id="element-1885" data-mp="2q4gsze63yk9g">
                  {locations.map(l => <li key={l.id} data-id="element-1886" data-mp="2i3so96y6p6b8">
                      <Link to={mapPath(l.mapId)} className={btn} data-id="element-1887" data-mp="2e1xf5ga0homw">
                        <MapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1888" data-mp="2e3qjgox5jwrq" /> {mapName(w, l.mapId)}
                      </Link>
                    </li>)}
                </ul> : <p className="flex items-start gap-2 rounded-2xl border border-white/10 p-4 text-gray-300" data-id="element-1889" data-mp="2n4yij97ht1mv">
                  <MapPinOff className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1890" data-mp="2x0rrkim06czw" />
                  {NPC_LOCATION_GAP}
                </p>}
            </WorldSection>

            <WorldSection id="servicios" title="Servicios" data-id="element-1891" data-mp="3au10yj9yayif">
              <div className="mb-4" data-id="element-1892" data-mp="35tn0bmaoiquf">
                <NpcQuestServices w={w} monsterId={m.id} data-id="element-1893" data-mp="3kqdpl2qn2m6b" />
              </div>
              <p className="flex items-start gap-2 text-gray-300" data-id="element-1894" data-mp="3kqm6r6tsodnb">
                <Store className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1895" data-mp="3fscaeswc3704" />
                {questsServedBy(w, m.id).length ? 'Otros servicios no documentados.' : 'Función no documentada en la configuración.'} {SHOP_INVENTORY_GAP}
              </p>
              <Link to="/guia/npc" className="mt-3 inline-flex font-semibold text-[#D4AF37] underline-offset-4 hover:underline" data-id="element-1896" data-mp="3pf5al8su2gxa">Ver NPC y tiendas</Link>
            </WorldSection>
          </>}

        {entry?.image?.origin === 'paquete' && <Disclosure title="Procedencia de la imagen" data-id="element-1897" data-mp="3fnivh1ddjxp9">
            <p className="text-sm text-gray-300" data-id="element-1898" data-mp="3kgbynkt72xz8">
              Foto de referencia visual del personaje (paquete {entry.key.startsWith('paquete-npc') ? 'VANGUARD_NPC' : 'VANGUARD_MOBS_LIMPIOS'}). Identifica al NPC; no es una captura de Vanguard ni confirma ubicación, inventario, precios ni que esté activo.
              Las fotos de Fanz se limpiaron con IA y pueden tener pequeñas diferencias de detalle.
            </p>
          </Disclosure>}
      </div>
    </div>;
}